/**
 * Checkout: the formula with reasons, removals, protein and flavor, add-ons, and a shipping
 * address (ONB-040 to ONB-044, ONB-046). Until the customer has confirmed their email and a
 * physician has signed, the page lists what is left instead of the address form and Place order.
 * Without Stripe keys the order completes in the pay request, no card.
 *
 * Whether the order is confirmed comes from the Worker's order rows, never from the URL. Stripe
 * sends the customer back to /checkout?paid=1, which only means "they may have paid": the page then
 * waits for the webhook to land and shows the confirmation once it has.
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import type { CheckoutIngredient, CheckoutStatus, CheckoutView, Flavor, PayBlocker, ShippingAddress } from "@lattice/core";
import { FLAVORS, formatUsd } from "@lattice/core";
import "@lattice/tokens/breakpoints.stylex";
import { breakpoints } from "@lattice/tokens/breakpoints.stylex";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/size.stylex";
import { size } from "@lattice/tokens/size.stylex";
import "@lattice/tokens/layer.stylex";
import { layer } from "@lattice/tokens/layer.stylex";
import "@lattice/tokens/product.stylex";
import { product } from "@lattice/tokens/product.stylex";
import "@lattice/tokens/space.stylex";
import { border, radius, space } from "@lattice/tokens/space.stylex";
import { paper } from "@lattice/tokens/themes";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { Banner } from "@lattice/ui/Banner";
import { Button } from "@lattice/ui/Button";
import { focusFirstError } from "@lattice/ui/focusFirstError";
import { Field } from "@lattice/ui/Field";
import { Input } from "@lattice/ui/Input";
import { RadioGroup } from "@lattice/ui/RadioGroup";
import { LinkButton } from "@lattice/ui/LinkButton";
import { Card } from "@lattice/ui/Card";
import { Content } from "@lattice/ui/Content";
import { Link } from "@lattice/ui/Link";
import { Mark } from "@lattice/ui/Mark";
import { Pending } from "@lattice/ui/Pending";
import { Status } from "@lattice/ui/Status";
import { Heading, Text } from "@lattice/ui/Text";
import type { CheckoutLoad, CheckoutResult } from "./api.ts";
import { checkCheckoutAddress, loadCheckout, removeCheckoutIngredient, requestCheckoutPay, restoreCheckoutIngredient, saveCheckoutOptions } from "./api.ts";
import { EmailCodeSignIn } from "./EmailCodeSignIn.tsx";
import { keepEntry } from "./router.ts";
const styles = {
  pouch: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1173whf",
    kmVPX3: "x1wg0vgl",
    kOBAk4: "x1gohwfw xs51665",
    ks0D6T: "x13trfab x1dho9p8",
    kUOVxO: "xvueqy4 x1rjmx0f",
    kzqmXN: "xh8yej3",
    kaIpWk: "x1fbwzx",
    kWkggS: "x1l0w1cl",
    kMwMTN: "xzb3lse",
    kVAM5u: "xtkjqb",
    ksu8eU: "x1y0btm7",
    kMzoRj: "xdq7b4a",
    $$css: true
  }
};
function formatDose(dose: number, unit: string): string {
  const shown = Number.isInteger(dose) ? dose.toLocaleString("en-US") : String(dose);
  return `${shown} ${unit}`;
}
function leadFor(status: CheckoutView["status"]): string {
  if (status === "approved") return "A physician signed this formula.";
  if (status === "in_review") return "A physician is reviewing this formula.";
  return "You can take ingredients out before you pay.";
}
const PAY_ERRORS = {
  incomplete: "Enter a street, city, state and ZIP.",
  not_us: "We ship inside the United States.",
  not_launch_state: "We do not ship to that state yet.",
  bad_zip: "That ZIP code does not look right.",
  missing_flavor: "Choose vanilla or chocolate.",
  missing_protein: "Choose a protein base.",
  not_approved: "A physician has to sign this formula before you can pay.",
  verified_account_required: "Confirm your email before you pay. Use the button in our email.",
  already_paid: "This order is already paid. Open your account to see it.",
  payment_not_configured: "Payment is not configured yet.",
  stripe_unavailable: "We could not start payment. Try again in a moment.",
  unsafe: "That change did not pass the safety checks.",
  cannot_remove_protein: "The protein base stays in every formula.",
  failed: "We could not save that. Try again in a moment."
} as const;
function messageFor(error: string): string {
  for (const [code, text] of Object.entries(PAY_ERRORS)) if (code === error) return text;
  return PAY_ERRORS.failed;
}
function Frame({
  kicker,
  children,
  aside
}: {
  readonly kicker: string;
  readonly children: ReactNode;
  readonly aside?: ReactNode;
}) {
  return <div {...{
    0: {
      className: "x1ov3xa9 x1mfo6jk xm8xz4y x1cikjda"
    },
    1: {
      className: "x1ov3xa9 x1mfo6jk xm8xz4y x1cikjda xv026ch x9azqnq"
    }
  }[!!(aside !== undefined) << 0]} data-testid="checkout-page">
      <Content width={aside === undefined ? "narrow" : "medium"}>
        <header className="x78zum5 x6s0dn4 x1qughib x1r0x45p">
          <a href="/" className="x78zum5 x6s0dn4 x1173whf x1envrbd x10dbofl x1hl2dhg xqu410z x8wwnw1" aria-label="Lattice home">
            <Mark size={22} />
            <span className="xm8xz4y">Lattice</span>
          </a>
          <Text as="span" variant="caption" tone="secondary">
            {kicker}
          </Text>
        </header>
        <div {...{
        0: {
          className: "xrvj5dj x1mkdm3x x1na3y3b x7a106z x1qs96fx xbnhjcb"
        },
        1: {
          className: "xrvj5dj x7a106z x1qs96fx xbnhjcb x1mkdm3x"
        }
      }[!!(aside === undefined) << 0]}>
          <main className="x78zum5 xdt5ytf x15agyyk xeuugli">{children}</main>
          {aside}
        </div>
      </Content>
    </div>;
}
function IngredientRow({
  item,
  busy,
  onRemove
}: {
  readonly item: CheckoutIngredient;
  readonly busy: boolean;
  readonly onRemove: (ingredientId: string) => void;
}) {
  return <li className="xrvj5dj xju1xpo x626il7 x7a106z x1wiri7k x8me8j4 x13fuv20 x153ygsw" data-testid={`ingredient-${item.ingredientId}`}>
      <div className="x78zum5 xdt5ytf x626il7">
        <Text>{item.name}</Text>
        <Text variant="caption" tone="secondary" numeric>
          {formatDose(item.dose, item.unit)}
        </Text>
        <Text tone="secondary">{item.reason}</Text>
        {item.priceCents !== null && <Text variant="caption" numeric>
            Add-on {formatUsd(item.priceCents)}
          </Text>}
      </div>
      <div className="x78zum5 xdt5ytf xuk3077 x1173whf">
        {!item.isProteinBase && <Button tone="quiet" size="small" busy={busy} ariaLabel={`Remove ${item.name}`} testId={`remove-${item.ingredientId}`} onClick={() => onRemove(item.ingredientId)}>
            Remove
          </Button>}
      </div>
    </li>;
}
function AddressFields({
  address,
  tried,
  onChange
}: {
  readonly address: ShippingAddress;
  /** Whether Place order was pressed, so an empty required field says so. */
  readonly tried: boolean;
  readonly onChange: (next: ShippingAddress) => void;
}) {
  const blank = (value: string, message: string) => tried && value.trim() === "" ? message : null;
  const set = (field: keyof ShippingAddress, value: string) => onChange({
    ...address,
    [field]: value
  });
  return <>
      <Field label="Street" error={blank(address.line1, "Enter the street address.")}>
        {control => <Input {...control} size="lg" autoComplete="address-line1" value={address.line1} onChange={value => set("line1", value)} />}
      </Field>
      <Field label="Apartment, suite, or unit" optional>
        {control => <Input {...control} size="lg" autoComplete="address-line2" value={address.line2} onChange={value => set("line2", value)} />}
      </Field>
      <Field label="City" error={blank(address.city, "Enter the city.")}>
        {control => <Input {...control} size="lg" autoComplete="address-level2" value={address.city} onChange={value => set("city", value)} />}
      </Field>
      <div className="xrvj5dj xcjjqqt x626il7 xpqajaz">
        <Field label="State" hint="Two letters, like CA." error={blank(address.state, "Enter the state, like CA.")}>
          {control => <Input {...control} size="lg" autoComplete="address-level1" maxLength={2} value={address.state} onChange={value => set("state", value)} />}
        </Field>
        <Field label="ZIP" error={blank(address.zip, "Enter the ZIP code.")}>
          {control => <Input {...control} size="lg" autoComplete="postal-code" inputMode="numeric" value={address.zip} onChange={value => set("zip", value)} />}
        </Field>
      </div>
    </>;
}
const EMPTY_ADDRESS: ShippingAddress = {
  line1: "",
  line2: "",
  city: "",
  state: "",
  zip: ""
};

/* How long a missing formula is asked for again, and how long a payment confirms before "slow". */
const WAIT_MS = 20_000;
const POLL_MS = 400;
const CONFIRM_POLL_MS = 1000;

/* After WAIT_MS a payment still confirming is asked about less often, for as long as the tab is open. */
const SLOW_POLL_MS = 5000;
type Load = "pending" | "signed_out" | "missing" | "declined" | "failed" | "ready";

/** One answer from /api/checkout, or "unreachable" when the request itself failed. */
type Fetched = CheckoutLoad | "unreachable";
async function fetchCheckout(): Promise<Fetched> {
  try {
    return await loadCheckout();
  } catch {
    return "unreachable";
  }
}
function viewOf(result: Fetched): CheckoutView | null {
  return result === "signed_out" || result === "missing" || result === "declined" || result === "failed" || result === "unreachable" ? null : result;
}
interface Step {
  readonly load: Load;
  readonly view: CheckoutView | null;
  /** The payment has been confirming for longer than WAIT_MS. */
  readonly slow: boolean;
  /** Milliseconds until the next request, or null to stop asking. */
  readonly again: number | null;
}
function emptyStep(result: Fetched, slow: boolean): Step {
  if (!slow) return {
    load: "pending",
    view: null,
    slow: false,
    again: POLL_MS
  };
  return {
    load: result === "missing" ? "missing" : "failed",
    view: null,
    slow: false,
    again: null
  };
}

/**
 * What one answer from /api/checkout means for the page. A 404 or a failed request is asked again
 * for WAIT_MS because the formula is drafted moments after the last answer. A customer back from
 * Stripe keeps asking while the payment is confirming, since the webhook that applies it can land
 * after they arrive. A failed request while a view is already on screen keeps that view and asks
 * again, so one bad answer during confirming never replaces it.
 */
function stepFor(result: Fetched, returned: boolean, elapsed: number, shown: CheckoutView | null): Step {
  if (result === "signed_out") return {
    load: "signed_out",
    view: null,
    slow: false,
    again: null
  };

  /* Nothing is coming, so there is nothing to wait for. */
  if (result === "declined") return {
    load: "declined",
    view: null,
    slow: false,
    again: null
  };
  const view = viewOf(result);
  const slow = elapsed > WAIT_MS;
  if (view === null && shown !== null) return {
    load: "ready",
    view: shown,
    slow,
    again: SLOW_POLL_MS
  };
  if (view === null) return emptyStep(result, slow);
  if (!returned || view.payment !== "confirming") return {
    load: "ready",
    view,
    slow: false,
    again: null
  };
  return {
    load: "ready",
    view,
    slow,
    again: slow ? SLOW_POLL_MS : CONFIRM_POLL_MS
  };
}

/*
  The page swaps whole states in place (confirming to confirmed), so focus would otherwise stay on a
  node that no longer exists and a screen reader would hear nothing change.
*/
function FocusedHeading({
  children
}: {
  readonly children: ReactNode;
}) {
  return <Heading level={1} focusOnMount>
      {children}
    </Heading>;
}
function SignedOut({
  returned
}: {
  readonly returned: boolean;
}) {
  return <Frame kicker="Checkout">
      <div className="x78zum5 xdt5ytf x626il7">
        <Heading level={1}>
          {returned ? "Sign in to see your order" : "Sign in to see your formula"}
        </Heading>
        <Text tone="secondary">
          {returned ? "If you finished paying, your order is saved. Sign in with the email you use for Lattice to see it." : "We email you a six-digit code, then bring you back here."}
        </Text>
      </div>
      <EmailCodeSignIn next={window.location.pathname + window.location.search} />
    </Frame>;
}
function NotReady() {
  return <Frame kicker="Checkout">
      <div className="x78zum5 xdt5ytf x626il7">
        <Heading level={1}>Your formula is not ready</Heading>
        <Text tone="secondary">
          A physician reviews it after your labs and answers. We email you when it is signed.
        </Text>
      </div>
      <div>
        <LinkButton href="/account" tone="quiet">
          Go to your account
        </LinkButton>
      </div>
    </Frame>;
}
function Declined() {
  return <Frame kicker="Checkout">
      <div className="x78zum5 xdt5ytf x626il7">
        <Heading level={1}>A physician could not sign this formula</Heading>
        <Text tone="secondary">
          Their note and what to do next are in your account. Nothing has been charged.
        </Text>
      </div>
      <div>
        <LinkButton href="/account" tone="quiet">
          Go to your account
        </LinkButton>
      </div>
    </Frame>;
}
function LoadFailed() {
  return <Frame kicker="Checkout">
      <div className="x78zum5 xdt5ytf x626il7">
        <Heading level={1}>We could not load checkout</Heading>
        <Text tone="secondary">
          Check your connection and try again. Nothing has been charged from this page.
        </Text>
      </div>
      <div>
        <Button testId="checkout-retry" onClick={() => window.location.reload()}>
          Reload checkout
        </Button>
      </div>
    </Frame>;
}
function Confirmed({
  view
}: {
  readonly view: CheckoutView;
}) {
  /*
    Drops ?paid=1 so a reload or a revisit days later shows "already ordered" rather than replaying
    "Paid" as if it had just happened.
  */
  useEffect(() => {
    keepEntry("/checkout");
  }, []);
  return <Frame kicker="Order confirmed" aside={<Totals view={view} totalLabel="Paid" />}>
      <div className="x78zum5 xdt5ytf x626il7" data-testid="order-confirmed">
        <FocusedHeading>Your order is confirmed</FocusedHeading>
        <Text tone="secondary">
          Your signed formula is on its way to the pharmacy, which mixes it to order. We email you
          when it ships.
        </Text>
        <Text tone="secondary">You can pause, skip a month or cancel from your account.</Text>
      </div>
      <div>
        <LinkButton href="/account">Go to your account</LinkButton>
      </div>
    </Frame>;
}

/* A customer who paid earlier and opens /checkout again, without coming back from Stripe. */
function AlreadyOrdered() {
  return <Frame kicker="Checkout">
      <div className="x78zum5 xdt5ytf x626il7" data-testid="already-ordered">
        <Heading level={1}>You have already ordered</Heading>
        <Text tone="secondary">Your orders, subscription and formula are in your account.</Text>
      </div>
      <div>
        <LinkButton href="/account">Go to your account</LinkButton>
      </div>
    </Frame>;
}
function Confirming({
  view,
  slow
}: {
  readonly view: CheckoutView;
  readonly slow: boolean;
}) {
  return <Frame kicker="Checkout" aside={<Totals view={view} totalLabel="Due today" />}>
      <div className="x78zum5 xdt5ytf x626il7" data-testid="payment-confirming">
        <FocusedHeading>Confirming your payment</FocusedHeading>
        <Status>
          {slow ? "This is taking longer than usual. If you finished paying, this page updates on its own and we email you." : "This usually takes a few seconds."}
        </Status>
      </div>
      {slow && <div className="x78zum5 xdt5ytf x626il7">
          <Text tone="secondary">
            Still waiting after a few minutes?{" "}
            <Link href="/support" variant="standalone">
              Contact support
            </Link>
          </Text>
          <Text tone="secondary">
            Left Stripe before paying?{" "}
            <Link href="/checkout" variant="standalone">
              Return to your formula
            </Link>
          </Text>
        </div>}
    </Frame>;
}
function signatureText(status: CheckoutStatus): string {
  return status === "in_review" ? "A physician is reviewing it now, usually within 24 hours. We email you when it is signed." : "This version has changes a physician has not signed yet.";
}
function BlockerItem({
  blocker,
  status
}: {
  readonly blocker: PayBlocker;
  readonly status: CheckoutStatus;
}) {
  if (blocker === "confirm_email") {
    return <li className="x78zum5 xdt5ytf x1cy8zhl x1173whf x1wiri7k x8me8j4 x13fuv20 x153ygsw x1g31smg" data-testid="waiting-on-confirm_email">
        <Text>Confirm your email</Text>
        <Text tone="secondary">
          Press the button in the email we sent you, or get a code here. Your order updates go to
          that address.
        </Text>
        <EmailCodeSignIn next="/checkout?confirmed=1" submitLabel="Confirm email" />
      </li>;
  }
  return <li className="x78zum5 xdt5ytf x1cy8zhl x1173whf x1wiri7k x8me8j4 x13fuv20 x153ygsw x1g31smg" data-testid="waiting-on-physician_signature">
      <Text>A physician signs your formula</Text>
      <Text tone="secondary">{signatureText(status)}</Text>
    </li>;
}
function BeforePaying({
  view
}: {
  readonly view: CheckoutView;
}) {
  return <Card title="Before you can pay" level={2} variant="inset">
      <Text tone="secondary">Nothing is charged until you place the order.</Text>
      <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf">
        {view.waitingOn.map(blocker => <BlockerItem key={blocker} blocker={blocker} status={view.status} />)}
      </ul>
    </Card>;
}
const ADDRESS_FIELDS = [["line1", "Street"], ["city", "City"], ["state", "State"], ["zip", "ZIP"]] as const;

/** "Street, City and ZIP". */
function listed(names: readonly string[]): string {
  const last = names.at(-1);
  return names.length < 2 || last === undefined ? names.join("") : `${names.slice(0, -1).join(", ")} and ${last}`;
}
function PayForm({
  address,
  formRef,
  problem,
  onChange,
  onPay
}: {
  readonly address: ShippingAddress;
  readonly problem: string | null;
  readonly formRef: RefObject<HTMLFormElement | null>;
  readonly onChange: (next: ShippingAddress) => void;
  readonly onPay: () => void;
}) {
  const [tried, setTried] = useState(false);
  const missing = ADDRESS_FIELDS.flatMap(([field, label]) => address[field].trim() === "" ? [label] : []);
  return <form ref={formRef} noValidate className="x78zum5 xdt5ytf x1r0x45p xh8yej3" onSubmit={event => {
    event.preventDefault();
    setTried(true);
    if (missing.length === 0) onPay();else focusFirstError(event.currentTarget);
  }}>
      <div className="x78zum5 xdt5ytf x626il7">
        <Heading level={2}>Ship to</Heading>
        <Text tone="secondary">A US address in a state we ship to.</Text>
      </div>
      {tried && missing.length >= 2 && <Status state="problem">{`${listed(missing)} need an answer.`}</Status>}
      <AddressFields address={address} tried={tried} onChange={onChange} />
      {problem !== null && <Status state="problem">{problem}</Status>}
    </form>;
}

/** The total and Place order, together in the summary. The button submits the address form. */
function PayBlock({
  total,
  busy,
  formRef
}: {
  readonly total: string;
  readonly busy: boolean;
  readonly formRef: RefObject<HTMLFormElement | null>;
}) {
  return <div className="x78zum5 xdt5ytf x626il7 xixxii4 xuqzskm x17y0mx6 x9u0ufj x1ey2m1c x1unuvze x1o8pag9 x1a91ssu x1bz5bhn x1mfo6jk xfpioka x8me8j4 x13fuv20 xiaongy x153ygsw">
      <p data-testid="due-before-button" role="status" className="x78zum5 x1qughib x626il7 x1ghz6dp xqu410z x1l1vl4p">
        <span>Due today</span>
        <span>{total}</span>
      </p>
      <Button testId="checkout-pay" busy={busy} wide onClick={() => formRef.current?.requestSubmit()}>
        Place order
      </Button>
    </div>;
}

/** True at the width where the summary sits beside the form instead of under it. */
function useBesideForm(): boolean {
  const query = "(min-width: 1024px)";
  const [beside, setBeside] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const list = window.matchMedia(query);
    const sync = () => setBeside(list.matches);
    list.addEventListener("change", sync);
    sync();
    return () => list.removeEventListener("change", sync);
  }, []);
  return beside;
}

/** The pouch this order ships, drawn from the customer's own formula. */
function OrderPouch({
  view
}: {
  readonly view: CheckoutView;
}) {
  const protein = view.proteinOptions.find(option => option.ingredientId === view.proteinBaseIngredientId);
  const flavor = view.flavor === null ? null : view.flavor === "vanilla" ? "Vanilla" : "Chocolate";
  const pouch = view.ingredients.filter(item => !item.separateAddon).length;
  const proteinItem = view.ingredients.find(item => item.isProteinBase);
  return <div data-testid="order-pouch" {...stylex.props(paper, styles.pouch)}>
      <span className="x78zum5 x6s0dn4 x1173whf xqu410z">
        <Mark size={22} />
        Lattice
      </span>
      <p className="x1ghz6dp xzb3lse xl4dl4e">
        {String(pouch)} {pouch === 1 ? "ingredient" : "ingredients"}, made for you from your labs
      </p>
      <p className="x1ghz6dp xr1yuqi xzb3lse x3cmmws xqu410z xj0a0fe">{protein?.name ?? "Choose a protein"}</p>
      {proteinItem && <p className="x1ghz6dp xzb3lse xl4dl4e">
          {formatDose(proteinItem.dose, proteinItem.unit)} a day
        </p>}
      <p className="x1ghz6dp xzb3lse xl4dl4e x78zum5 x6s0dn4 x1173whf">
        <span aria-hidden="true" {...{
        0: {
          className: "x2lah0s x1vd0ds5 x1sqxqiv x1m3fsnl xtkjqb x1y0btm7 xdq7b4a"
        },
        4: {
          className: "x2lah0s x1vd0ds5 x1sqxqiv x1m3fsnl xtkjqb xdq7b4a xjbqb8w xbsl7fq"
        },
        2: {
          className: "x2lah0s x1vd0ds5 x1sqxqiv x1m3fsnl xtkjqb x1y0btm7 xdq7b4a x1p82lfq"
        },
        6: {
          className: "x2lah0s x1vd0ds5 x1sqxqiv x1m3fsnl xtkjqb xdq7b4a xbsl7fq x1p82lfq"
        },
        1: {
          className: "x2lah0s x1vd0ds5 x1sqxqiv x1m3fsnl xtkjqb x1y0btm7 xdq7b4a x1h3r81n"
        },
        5: {
          className: "x2lah0s x1vd0ds5 x1sqxqiv x1m3fsnl xtkjqb xdq7b4a xbsl7fq x1h3r81n"
        },
        3: {
          className: "x2lah0s x1vd0ds5 x1sqxqiv x1m3fsnl xtkjqb x1y0btm7 xdq7b4a x1h3r81n"
        },
        7: {
          className: "x2lah0s x1vd0ds5 x1sqxqiv x1m3fsnl xtkjqb xdq7b4a xbsl7fq x1h3r81n"
        }
      }[!!(flavor === null) << 2 | !!(flavor === "Vanilla") << 1 | !!(flavor === "Chocolate") << 0]} />
        Flavor: {flavor ?? "choose one"}
      </p>
      <div className="x78zum5 xdt5ytf xskm3c9 xap4y5k xez8t42 x13fuv20 x153ygsw">
        <p className="x1ghz6dp xzb3lse xl4dl4e">Signed by a Lattice physician</p>
      </div>
    </div>;
}
function Totals({
  view,
  totalLabel,
  pay,
  pouch = true
}: {
  readonly view: CheckoutView;
  readonly totalLabel: string;
  /** The total and Place order, kept in the summary so they are never apart. */
  readonly pay?: ReactNode;
  /** Whether the pouch is drawn here, or above the formula list on a narrower screen. */
  readonly pouch?: boolean;
}) {
  return <aside className="x1uhb9sk xqr1kp1 x3vkroj">
      <Card title="Your order" level={2} variant="inset">
        <div className="x78zum5 xdt5ytf x1r0x45p">
          {pouch && <OrderPouch view={view} />}
          <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf x626il7">
            <li>
              <Text tone="secondary">Your pouch ships every month.</Text>
            </li>
            <li>
              <Text tone="secondary">A physician reviews it again when you upload new labs.</Text>
            </li>
            <li>
              <Text tone="secondary">We email a check-in before each renewal.</Text>
            </li>
          </ul>
          <div className="x78zum5 xdt5ytf x1173whf">
            <p className="x78zum5 x1qughib x626il7 x1ghz6dp x1l1vl4p">
              <span>Formula</span>
              <span>{formatUsd(view.monthlyCents)}</span>
            </p>
            {view.addonsCents > 0 && <p className="x78zum5 x1qughib x626il7 x1ghz6dp x1l1vl4p">
                <span>Add-ons</span>
                <span>{formatUsd(view.addonsCents)}</span>
              </p>}
            {pay === undefined && <p className="x78zum5 x1qughib x626il7 x1ghz6dp x1l1vl4p xqu410z">
                <span>{totalLabel}</span>
                <span>{formatUsd(view.totalCents)}</span>
              </p>}
          </div>
          {pay}
        </div>
      </Card>
    </aside>;
}
interface Note {
  readonly state: "ok" | "problem";
  readonly text: string;
  /** Pay errors sit beside Place order. Everything else reports under the heading. */
  readonly at: "top" | "pay";
}

/* Refusals that mean the page is out of date: it reloads the view, which shows why. */
const STALE = new Set(["verified_account_required", "not_approved", "already_paid"]);

/** What changed when a refresh cleared something the customer was waiting on. */
function clearedNote(before: CheckoutView, after: CheckoutView): Note | null {
  const was = new Set(before.waitingOn);
  const now = new Set(after.waitingOn);
  if (was.size === now.size) return null;
  if (now.size === 0) return {
    state: "ok",
    text: "You can pay now.",
    at: "top"
  };
  if (was.has("confirm_email") && !now.has("confirm_email")) return {
    state: "ok",
    text: "Your email is confirmed.",
    at: "top"
  };
  return null;
}

/*
  A removal from a signed formula swaps the address form for "Before you can pay", and putting the
  ingredient back swaps it in again. Without a line saying why, the form seems to vanish and return.
*/
function signatureNote(before: CheckoutView, after: CheckoutView): Note | null {
  if (before.status === "approved" && after.status === "in_review") return {
    state: "ok",
    text: "A physician reviews this change before you can pay. Put it back to pay for the formula they signed.",
    at: "top"
  };
  if (before.status === "in_review" && after.status === "approved") return {
    state: "ok",
    text: "This is the formula your physician signed.",
    at: "top"
  };
  return null;
}

/** What the URL says about how the customer got here. None of it is trusted for order state. */
interface Arrival {
  /** Stripe's success URL, `?paid=1`. */
  readonly returned: boolean;
  /** Stripe's cancel URL, `?left=1`. */
  readonly left: boolean;
  /** The confirm-email code step sends the customer back with `?confirmed=1`. */
  readonly emailConfirmed: boolean;
}
function arrivalOf(search: string): Arrival {
  const query = new URLSearchParams(search);
  return {
    returned: query.get("paid") === "1",
    left: query.get("left") === "1",
    emailConfirmed: query.get("confirmed") === "1"
  };
}

/*
  A pending order outlives a Stripe session the customer abandoned, so "confirming" without a return
  from Stripe may be a payment still settling or one that never will. The line asks them to wait
  rather than inviting a second payment that could charge twice.
*/
function arrivalNote(view: CheckoutView, arrival: Arrival): Omit<Note, "at"> | null {
  if (arrival.left) return {
    state: "ok",
    text: "You left Stripe before paying. Nothing was charged."
  };
  if (arrival.returned && view.payment === "unpaid") return {
    state: "problem",
    text: "We found no payment for this order. You can place it below."
  };
  if (view.payment === "confirming") {
    return {
      state: "problem",
      text: "A payment you started on Stripe has not been confirmed. If you paid, wait for our email before you order again."
    };
  }
  if (arrival.emailConfirmed && !view.waitingOn.includes("confirm_email")) return {
    state: "ok",
    text: "Your email is confirmed."
  };
  return null;
}
type Handler<T> = (value: T) => void;
function FormulaPage({
  view,
  busy,
  note,
  address,
  arrival,
  onAddress,
  onPay,
  onRemove,
  onRestore,
  onOptions
}: {
  readonly view: CheckoutView;
  readonly busy: boolean;
  readonly note: Note | null;
  readonly address: ShippingAddress;
  readonly arrival: Arrival;
  readonly onAddress: Handler<ShippingAddress>;
  readonly onPay: () => void;
  readonly onRemove: Handler<string>;
  readonly onRestore: Handler<string>;
  readonly onOptions: Handler<{
    readonly flavor?: Flavor;
    readonly proteinBaseIngredientId?: string;
  }>;
}) {
  const blocked = view.waitingOn.length > 0;
  const pouch = view.ingredients.filter(item => !item.separateAddon);
  const addons = view.ingredients.filter(item => item.separateAddon);
  const arrived = arrivalNote(view, arrival);
  const formRef = useRef<HTMLFormElement>(null);
  const beside = useBesideForm();

  /* The pinned bar hides what it covers, so a focused field scrolls clear of it. */
  useEffect(() => {
    if (beside || blocked) return;
    const root = document.documentElement;
    root.style.scrollPaddingBottom = "10rem";
    return () => {
      root.style.scrollPaddingBottom = "";
    };
  }, [beside, blocked]);
  return <Frame kicker="Checkout" aside={<Totals view={view} totalLabel="Due today" pouch={beside} pay={blocked ? undefined : <PayBlock total={formatUsd(view.totalCents)} busy={busy} formRef={formRef} />} />}>
      <div className="x78zum5 xdt5ytf x626il7">
        <Heading level={1}>Your formula</Heading>
        {!blocked && <Text tone="secondary">{leadFor(view.status)}</Text>}
      </div>
      {!beside && <OrderPouch view={view} />}
      {arrived && <Banner tone={arrived.state === "ok" ? "info" : "warn"}>{arrived.text}</Banner>}
      {note?.at === "top" && <Banner tone={note.state === "ok" ? "info" : "danger"}>{note.text}</Banner>}
      {blocked && <BeforePaying view={view} />}
      <section className="x78zum5 xdt5ytf x626il7" aria-labelledby="protein">
        <Heading level={2} id="protein">
          Protein
        </Heading>
        <Text tone="secondary">Choose your protein.</Text>
        <RadioGroup labelledBy="protein" variant="cards" orientation="horizontal" manual loading={busy} options={view.proteinOptions.map(option => ({
        value: option.ingredientId,
        label: option.name,
        testId: `protein-${option.ingredientId}`
      }))} value={view.proteinBaseIngredientId} onChange={ingredientId => onOptions({
        proteinBaseIngredientId: ingredientId
      })} />
      </section>
      <section className="x78zum5 xdt5ytf x626il7" aria-labelledby="flavor">
        <Heading level={2} id="flavor">
          Flavor
        </Heading>
        {view.flavor === null && <Text tone="secondary" id="flavor-missing">
            Choose one before you order.
          </Text>}
        <RadioGroup labelledBy="flavor" variant="cards" orientation="horizontal" describedBy={view.flavor === null ? "flavor-missing" : undefined} manual loading={busy} options={FLAVORS.map((flavor: Flavor) => ({
        value: flavor,
        label: flavor === "vanilla" ? "Vanilla" : "Chocolate",
        testId: `flavor-${flavor}`
      }))} value={view.flavor} onChange={flavor => onOptions({
        flavor
      })} />
      </section>
      <section className="x78zum5 xdt5ytf x626il7" aria-labelledby="in-pouch">
        <Heading level={2} id="in-pouch">
          In the pouch
        </Heading>
        <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf">
          {pouch.map(item => <IngredientRow key={item.ingredientId} item={item} busy={busy} onRemove={onRemove} />)}
        </ul>
      </section>
      {addons.length > 0 && <section className="x78zum5 xdt5ytf x626il7" aria-labelledby="addons">
          <Heading level={2} id="addons">
            Add-ons, shipped separately
          </Heading>
          <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf">
            {addons.map(item => <IngredientRow key={item.ingredientId} item={item} busy={busy} onRemove={onRemove} />)}
          </ul>
        </section>}
      {view.restorable.length > 0 && <section className="x78zum5 xdt5ytf x626il7" aria-labelledby="removed">
          <Heading level={2} id="removed">
            Removed
          </Heading>
          <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf">
            {view.restorable.map(item => <li key={item.ingredientId} className="xrvj5dj xju1xpo x626il7 x7a106z x1wiri7k x8me8j4 x13fuv20 x153ygsw">
                <Text>{item.name}</Text>
                <Button tone="quiet" size="small" busy={busy} ariaLabel={`Put back ${item.name}`} testId={`restore-${item.ingredientId}`} onClick={() => onRestore(item.ingredientId)}>
                  Put back
                </Button>
              </li>)}
          </ul>
        </section>}
      {!blocked && <PayForm address={address} problem={note?.at === "pay" ? note.text : null} formRef={formRef} onChange={onAddress} onPay={onPay} />}
    </Frame>;
}
function titleFor(load: Load, view: CheckoutView | null, returned: boolean): string {
  if (load === "signed_out") return "Sign in · Checkout · Lattice";
  if (view === null) return "Checkout · Lattice";
  if (view.payment === "confirmed") return returned ? "Order confirmed · Lattice" : "Already ordered · Lattice";
  if (returned && view.payment === "confirming") return "Confirming payment · Lattice";
  return view.waitingOn.length > 0 ? "Before you can pay · Checkout · Lattice" : "Checkout · Lattice";
}
export function Checkout() {
  const [view, setView] = useState<CheckoutView | null>(null);
  const [load, setLoad] = useState<Load>("pending");
  const [slow, setSlow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<Note | null>(null);
  const [address, setAddress] = useState(EMPTY_ADDRESS);

  /* Read once: Confirmed rewrites the URL, and `returned` must not flip while the page is open. */
  const [arrival] = useState(() => arrivalOf(window.location.search));
  const {
    returned
  } = arrival;

  /* Read by the focus refresh, which must not overwrite an edit that is still in flight. */
  const live = useRef({
    view,
    busy,
    refreshing: false
  });
  useEffect(() => {
    live.current.view = view;
    live.current.busy = busy;
  }, [view, busy]);
  const blocked = view !== null && view.waitingOn.length > 0;
  const title = titleFor(load, view, returned);
  useEffect(() => {
    document.title = title;
  }, [title]);
  useEffect(() => {
    let cancelled = false;
    let timer = 0;
    let shown: CheckoutView | null = null;
    const started = Date.now();
    const tick = async () => {
      const result = await fetchCheckout();
      if (cancelled) return;
      const step = stepFor(result, returned, Date.now() - started, shown);
      shown = step.view;
      setLoad(step.load);
      setView(step.view);
      setSlow(step.slow);
      if (step.again !== null) timer = window.setTimeout(() => void tick(), step.again);
    };
    void tick();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [returned]);

  /*
    The email is confirmed in another tab and the physician signs while this one sits open, so a
    blocked page asks again whenever the customer comes back to it. Focus and visibilitychange both
    fire on return, so a refresh already running is not started twice.
  */
  useEffect(() => {
    if (!blocked) return;
    const refresh = () => {
      const state = live.current;
      if (document.visibilityState !== "visible" || state.busy || state.refreshing) return;
      state.refreshing = true;
      void fetchCheckout().then(result => {
        state.refreshing = false;
        const before = state.view;
        const next = viewOf(result);
        if (next === null || before === null || state.busy) return;
        setView(next);
        const cleared = clearedNote(before, next);
        if (cleared !== null) setNote(cleared);
      });
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [blocked]);

  /* `work` answers true when it is leaving the page, so Place order stays busy until it unloads. */
  const run = async (work: () => Promise<boolean>) => {
    if (busy) return;
    setBusy(true);
    setNote(null);
    const leaving = await work();
    if (!leaving) setBusy(false);
  };
  const apply = async (request: () => Promise<CheckoutResult>): Promise<boolean> => {
    const before = view;
    const result = await request().catch(() => ({
      ok: false as const,
      error: "failed"
    }));
    if (!result.ok) {
      setNote({
        state: "problem",
        text: messageFor(result.error),
        at: "top"
      });
      return false;
    }
    setView(result.view);
    const changed = before === null ? null : signatureNote(before, result.view);
    if (changed !== null) setNote(changed);
    return false;
  };
  const payProblem = async (error: string): Promise<boolean> => {
    if (!STALE.has(error)) {
      setNote({
        state: "problem",
        text: messageFor(error),
        at: "pay"
      });
      return false;
    }
    const next = viewOf(await fetchCheckout());
    if (next !== null) setView(next);
    setNote({
      state: "problem",
      text: messageFor(error),
      at: "top"
    });
    return false;
  };

  /*
    Both a Stripe session and a payment completed in this request go through /checkout?paid=1, so
    the confirmation is always read back from the Worker.
  */
  const placeOrder = () => void run(async () => {
    try {
      const problem = await checkCheckoutAddress(address);
      if (problem) return await payProblem(problem);
      const paid = await requestCheckoutPay(address);
      if (!paid.ok) return await payProblem(paid.error);
      window.location.assign("url" in paid ? paid.url : "/checkout?paid=1");
      return true;
    } catch {
      return payProblem("stripe_unavailable");
    }
  });
  if (load === "pending") {
    return <Frame kicker="Checkout">
        <Pending message={returned ? "Checking your payment" : "Loading your formula"} bones={["title", "line", "line", "control"]} />
      </Frame>;
  }
  if (load === "signed_out") return <SignedOut returned={returned} />;
  if (load === "failed") return <LoadFailed />;
  if (load === "declined") return <Declined />;
  if (load === "missing" || !view) return <NotReady />;
  if (view.payment === "confirmed") return returned ? <Confirmed view={view} /> : <AlreadyOrdered />;
  if (returned && view.payment === "confirming") return <Confirming view={view} slow={slow} />;
  return <FormulaPage view={view} busy={busy} note={note} address={address} arrival={arrival} onAddress={setAddress} onPay={placeOrder} onRemove={id => void run(async () => apply(async () => removeCheckoutIngredient(id)))} onRestore={id => void run(async () => apply(async () => restoreCheckoutIngredient(id)))} onOptions={choice => void run(async () => apply(async () => saveCheckoutOptions(choice)))} />;
}